import type { Agent, AgentStatus } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { Archive, Check, Copy, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { skillsApi, studioApi } from "@/api/studio.ts";
import { cn } from "@/lib/cn.ts";
import { AgentAvatar } from "@/ui/AgentAvatar.tsx";
import { Alert, Button, Input, PageTitle } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";

/** Studio shell: Agents / Groups / Skills tabs. */
export function AgentStudio() {
  const { t } = useTranslation();
  // a dot on the Skills tab when a GitHub collection has a newer commit
  const cols = useQuery({ queryKey: ["studio", "skills", "collections"], queryFn: () => skillsApi.collections(), refetchInterval: 5 * 60_000 });
  const skillUpdates = (cols.data ?? []).some((c) => c.updateAvailable);
  const tabs = [
    { to: "/agents", label: t("studio.tabs.agents"), exact: true, dot: false },
    { to: "/agents/groups", label: t("studio.tabs.groups"), dot: false },
    { to: "/agents/skills", label: t("studio.tabs.skills"), dot: skillUpdates },
  ];
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto min-h-[calc(100%+1px)] max-w-6xl space-y-5 px-4 py-6 sm:px-8">
        <div className="flex items-center justify-center">
          <nav className="inline-flex rounded-lg border border-border bg-surface p-1">
            {tabs.map((tab) => (
              <Link key={tab.to} to={tab.to} activeOptions={{ exact: !!tab.exact }} className="rounded-md px-4 py-1.5 text-sm text-fg-muted hover:text-fg" activeProps={{ className: "bg-accent-soft text-fg font-medium" }}>
                {tab.label}
                {tab.dot && <span className="ms-1.5 inline-block size-2 rounded-full bg-warn align-middle" title={t("studio.skills.updateAvailable")} />}
              </Link>
            ))}
          </nav>
        </div>
        <Outlet />
      </div>
    </div>
  );
}

const FILTERS: { key: "all" | AgentStatus; label: string }[] = [
  { key: "all", label: "all" },
  { key: "ACTIVE", label: "active" },
  { key: "DRAFT", label: "draft" },
  { key: "ARCHIVED", label: "archived" },
];

export function AgentsList() {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ["studio", "agents"], queryFn: studioApi.agents });
  const [filter, setFilter] = useState<"all" | AgentStatus>("all");
  const [search, setSearch] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["studio", "agents"] });
  const setStatus = useMutation({ mutationFn: ({ id, status }: { id: string; status: AgentStatus }) => studioApi.setStatus(id, status), onSuccess: refresh });
  const clone = useMutation({ mutationFn: studioApi.clone, onSuccess: (a) => { void refresh(); void nav({ to: "/agents/$agentId", params: { agentId: a.id } }); } });
  const remove = useMutation({ mutationFn: studioApi.removeAgent, onSuccess: refresh });
  const { confirm } = useConfirm();
  const askRemove = async (a: { id: string; name: string }) => { if (await confirm({ title: t("confirm.deleteAgentTitle"), message: t("confirm.deleteAgentMsg", { name: a.name }), confirmLabel: t("common.delete"), danger: true })) remove.mutate(a.id); };
  const all = q.data ?? [];
  const needle = search.trim().toLowerCase();
  const shown = all.filter((a) => (filter === "all" ? a.status !== "ARCHIVED" : a.status === filter)).filter((a) => !needle || a.name.toLowerCase().includes(needle) || a.handle.includes(needle) || a.title.toLowerCase().includes(needle));
  const counts: Record<"all" | AgentStatus, number> = { all: all.filter((a) => a.status !== "ARCHIVED").length, ACTIVE: all.filter((a) => a.status === "ACTIVE").length, DRAFT: all.filter((a) => a.status === "DRAFT").length, DISABLED: all.filter((a) => a.status === "DISABLED").length, ARCHIVED: all.filter((a) => a.status === "ARCHIVED").length };
  const err = setStatus.error ?? clone.error ?? remove.error;

  return (
    <div className="space-y-4">
      <PageTitle title={t("studio.agents.title")} subtitle={t("studio.agents.subtitle")} />
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)} className={cn("rounded-full border px-3 py-1 text-xs", filter === f.key ? "border-accent bg-accent-soft" : "border-border hover:bg-surface-2")}>
            {t(`studio.filters.${f.label}`)} <span className="ms-1 text-fg-muted">{counts[f.key]}</span>
          </button>
        ))}
        <div className="relative ms-auto">
          <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-fg-muted" />
          <Input className="h-8 w-56 ps-8 text-xs" placeholder={t("studio.agents.search")} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Button size="sm" onClick={() => void nav({ to: "/agents/new" })}><Plus className="size-4" /> {t("studio.agents.new")}</Button>
      </div>
      {err && <Alert tone="danger">{toMsg(err)}</Alert>}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {shown.map((a) => (
          <AgentCard key={a.id} a={a} onEdit={() => void nav({ to: "/agents/$agentId", params: { agentId: a.id } })} onClone={() => clone.mutate(a.id)} onActivate={() => setStatus.mutate({ id: a.id, status: a.status === "ACTIVE" ? "DISABLED" : "ACTIVE" })} onArchive={() => setStatus.mutate({ id: a.id, status: a.status === "ARCHIVED" ? "DRAFT" : "ARCHIVED" })} onDelete={() => void askRemove(a)} />
        ))}
      </div>
      {shown.length === 0 && <p className="text-center text-sm text-fg-muted">{t("studio.agents.empty")}</p>}
    </div>
  );
}

function permissionChips(a: Agent): { label: string; tone: "info" | "warn" | "muted" }[] {
  const p = a.permissions;
  const chips: { label: string; tone: "info" | "warn" | "muted" }[] = [];
  if (p.read) chips.push({ label: "read", tone: "muted" });
  if (p.edit) chips.push({ label: "edit", tone: "info" });
  if (p.run) chips.push({ label: "run", tone: "info" });
  if (p.network) chips.push({ label: "network", tone: "info" });
  if (p.browser) chips.push({ label: "browser", tone: "info" });
  if (p.secrets) chips.push({ label: "secrets", tone: "warn" });
  for (const [k, v] of Object.entries(p.approvals)) if (v) chips.push({ label: `approve:${k}`, tone: "info" });
  return chips;
}

export function AgentCard({ a, onEdit, onClone, onActivate, onArchive, onDelete, compact }: { a: Agent; onEdit?: () => void; onClone?: () => void; onActivate?: () => void; onArchive?: () => void; onDelete?: () => void; compact?: boolean }) {
  const { t } = useTranslation();
  const statusTone = { DRAFT: "bg-warn-soft text-fg", ACTIVE: "bg-success-soft text-success", DISABLED: "bg-surface-2 text-fg-muted", ARCHIVED: "bg-surface-2 text-fg-muted" }[a.status];
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-surface" style={{ borderInlineStartWidth: 3, borderInlineStartColor: a.avatar.color }}>
      <div className="flex items-start gap-3 p-4">
        <AgentAvatar avatar={a.avatar} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2"><span className="truncate font-semibold">{a.name}</span><span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium uppercase", statusTone)}>{t(`studio.status.${a.status}`)}</span></div>
          <div dir="ltr" className="text-start font-mono text-xs text-fg-muted">@{a.handle}{a.title ? ` · ${a.title}` : ""}</div>
          {a.description && <p className="mt-1 line-clamp-2 text-xs text-fg-muted">{a.description}</p>}
        </div>
      </div>
      {!compact && (
        <>
          <dl className="grid grid-cols-2 gap-px border-y border-border bg-border text-xs">
            {[
              [t("studio.card.model"), `${a.model.provider} · ${a.model.model === "default" ? t("chat.models.default") : a.model.model}${a.model.speed === "fast" ? ` · ${t("chat.speed.fast")}` : ""}`],
              [t("studio.card.thinking"), a.model.thinking],
              [t("studio.card.skills"), String(a.skillIds.length)],
              [t("studio.card.tasks"), String(a.tasks)],
            ].map(([k, v]) => (
              <div key={k} className="bg-surface px-4 py-2"><dt className="text-[10px] uppercase tracking-wide text-fg-muted">{k}</dt><dd>{v}</dd></div>
            ))}
          </dl>
          <div className="flex flex-wrap gap-1 px-4 py-2">
            {permissionChips(a).map((c) => <span key={c.label} className={cn("rounded px-1.5 py-0.5 font-mono text-[10px] uppercase", c.tone === "warn" ? "bg-danger-soft text-danger" : c.tone === "info" ? "bg-accent-soft" : "bg-surface-2 text-fg-muted")}>{c.label}</span>)}
          </div>
          <div className="mt-auto grid grid-cols-5 divide-x divide-border border-t border-border rtl:divide-x-reverse">
            <button type="button" className="p-2 text-fg-muted hover:bg-surface-2 hover:text-fg" title={t("studio.actions.edit")} onClick={onEdit}><Pencil className="mx-auto size-4" /></button>
            <button type="button" className="p-2 text-fg-muted hover:bg-surface-2 hover:text-fg" title={t("studio.actions.clone")} onClick={onClone}><Copy className="mx-auto size-4" /></button>
            <button type="button" className="p-2 text-fg-muted hover:bg-surface-2 hover:text-fg" title={a.status === "ACTIVE" ? t("studio.actions.disable") : t("studio.actions.activate")} onClick={onActivate}><Check className={cn("mx-auto size-4", a.status === "ACTIVE" && "text-success")} /></button>
            <button type="button" className="p-2 text-fg-muted hover:bg-surface-2 hover:text-fg" title={a.status === "ARCHIVED" ? t("studio.actions.unarchive") : t("studio.actions.archive")} onClick={onArchive}><Archive className="mx-auto size-4" /></button>
            <button type="button" className="p-2 text-fg-muted hover:bg-surface-2 hover:text-danger" title={t("common.delete")} onClick={onDelete}><Trash2 className="mx-auto size-4" /></button>
          </div>
        </>
      )}
    </div>
  );
}
